const {DataSet, Network} = require('vis-network/standalone');
const Utils = require('./utils.js');

const GraphMode = {
   Edit: 0,
   Selection: 1,
   Link: 2,
};

class GraphManager{
   constructor(){
      this.curNetwork = null;
      this.curContainer = null;
      this.curNodes = new DataSet();
      this.curEdges = new DataSet();
      this.selectionStartPoint = null;
      this.selectionCurPoint = null;
      this.curHoverNode = null;
      this.highlightLink = false;
      this.linkStartNode = null;
      this.curMode = GraphMode.Edit;
      this.prevMode = null;
      this.onHighlightedNodeChange = () => {};
      this.addEdgeFunction = (idNodeTo, idNodeFrom) => {};

      this.options = {
         locales: {
            en:{
               edit: 'Edit',
               del: 'Delete selected',
               back: 'Back',
               addEdge: 'Link Evidence',
               edgeDescription: 'Click on a piece of evidence and drag the edge to another to connect them.',
            }
         },
         nodes: {
            borderWidth: 2,
            widthConstraint: {
               maximum: 200
            },
            font: {
               color: "rgba(0, 0, 0, 1)",
               size: 16,
               face: "avenir",
               background: "rgba(250, 250, 250, 1)",
            }
         },
         edges:{
            smooth: {
               type: 'continuous',
            },
            color: "rgba(11, 189, 209, 1)",
            hoverWidth: 2
         },
         width:`${window.innerWidth}`,
         height: `${window.innerHeight * .8}`,
         physics: {
            barnesHut: {
               springLength: 100,
               springConstant: 0.1,
               centralGravity: 0.2,
               avoidOverlap: 0,
            },
            timestep: 0.75,
            stabilization: {
               iterations: 250,
            },
            solver: 'barnesHut'
         },
         manipulation: {
            addNode: false,
            addEdge: function (data, callback) {
               callback(data);
            },
            deleteNode: false,
            deleteEdge: false,
            editEdge: false,
         },
         interaction: {
            hover: true,
            hideEdgesOnDrag: false,
            dragView: true,
            multiselect: false,
         },

      }
   }

   initNetwork(){
      let data = {
         nodes: this.curNodes,
         edges: this.curEdges
      };

      this.curNetwork = new Network(this.curContainer, data, this.options);

      this.curNetwork.on("hoverNode", (params) => {
         if(params.node.substring(0,1) === 'b') {
            let position = this.curNetwork.getPositions(params.node)[params.node];
            position.x += 10;
            position.y -= 20;
            this.onHighlightedNodeChange(this.curNetwork.canvasToDOM(position));
         }
         else{
            this.highlightLink = this.linkStartNode && this.linkStartNode !== params.node
         }

         let node = this.curNodes.get(params.node);
         this.curHoverNode = params.node;
         this.curNodes.update({id: params.node, label: node.text});
      });

      this.curNetwork.on("blurNode", (params) => {
         this.highlightLink = false;
         this.onHighlightedNodeChange(null);
         let node = this.curNodes.get(params.node);
         this.curNodes.update({id: params.node, label: Utils.getPreviewFromText(node.text)});
      });

      this.curNetwork.on("dragEnd", (params) => {
         this.startLink()
            this.curMode = this.prevMode;
            let endNode = this.curNetwork.getNodeAt(params.pointer.DOM);
            let startNode = this.linkStartNode;
            if( startNode && endNode && (startNode !== endNode)){
               this.addEdgeFunction(this.linkStartNode, endNode);
            }
            this.linkStartNode = null;
      });

      
      this.curNetwork.on("dragStart", (params) => {
         if(this.curMode === GraphMode.Selection) {
            this.selectionStartPoint = params.pointer.canvas;
         }
      });

      this.curNetwork.on("mouseMove", (params) => {
         if(this.curMode === GraphMode.Link){
            this.linkCurPoint = params.pointer.canvas;
            this.curNetwork.redraw();
         }
      });

      this.curNetwork.on("dragging", (params) => {
         if(this.curMode === GraphMode.Selection) {
            this.selectionCurPoint = params.pointer.canvas;
            this.curNetwork.redraw();
         }
         if(params.nodes.length > 0 && params.nodes[0].substring(0,1) === 'b') {
            let position = this.curNetwork.getPositions(params.nodes[0])[params.nodes[0]];
            position.x += 10;
            position.y -= 20;
            this.onHighlightedNodeChange(this.curNetwork.canvasToDOM(position));
         }
      });

      this.curNetwork.on("dragEnd", (params) => {
         if(this.curMode === GraphMode.Selection) {
            let bounds = Utils.calculateBoundsForPoints(this.selectionStartPoint, params.pointer.canvas);
            let nodePositionData = this.curNetwork.getPositions();
            let selectedNodes = [];

            Object.keys(nodePositionData).forEach((node) => {
               let position = nodePositionData[node];
               if (Utils.isPointInBounds(position, bounds))
                  selectedNodes.push(node);
            });

            this.curNetwork.selectNodes(selectedNodes);
            this.selectionStartPoint = null;
            this.selectionCurPoint = null;
         }
      });

      this.curNetwork.on("beforeDrawing", (ctx) => {
         if(this.curMode === GraphMode.Link){
            if(this.highlightLink){
               ctx.strokeStyle = '#ffec00';
               ctx.lineWidth = 4;
            }
            else {
               ctx.strokeStyle = '#294475';
               ctx.lineWidth = 2;
            }
            ctx.beginPath();
            ctx.moveTo(this.linkStartPoint.x, this.linkStartPoint.y);
            ctx.lineTo(this.linkCurPoint.x, this.linkCurPoint.y);
            ctx.stroke();
         }
      });

      this.curNetwork.on("afterDrawing", (ctx) => {
         if(this.curMode === GraphMode.Selection) {
            if (!this.selectionStartPoint || !this.selectionCurPoint) return;
            let bounds = Utils.calculateBoundsForPoints(this.selectionStartPoint, this.selectionCurPoint);
            let width = bounds.right - bounds.left;
            let height = bounds.top - bounds.bottom;

            ctx.strokeStyle = '#294475';
            ctx.lineWidth = 4;
            ctx.fillStyle = '#A6D5F7';
            ctx.rect(bounds.left, bounds.bottom, width, height);
            ctx.stroke();
         }
      });
   }

   zoomOnCard(cardType, idCard, selectNodeFunction){
      let nodeId = '';
      if(cardType === 'evidence'){
         nodeId += 'b'
      }
      else if(cardType === 'stimulus'){
         nodeId += 's'
      }
      else{
         nodeId += 'g'
      }
      nodeId += idCard;
      let animationOptions = {
        scale: 1,
        offset: {x:0, y:0},
        animation: {
          duration: 2000,
          easingFunction: 'easeInOutQuad'
        }
      };
      this.curNetwork.focus(nodeId, animationOptions);
      this.curNetwork.selectNodes([nodeId], true);
      selectNodeFunction(nodeId)
   }

   setEditMode(){
      this.options.interaction.multiselect = false;
      this.options.interaction.dragView = true;
      this.curMode = GraphMode.Edit;
      this.curNetwork.setOptions(this.options);
   }

   setSelectionMode(){
      this.options.interaction.multiselect = true;
      this.options.interaction.dragView = false;
      this.curMode = GraphMode.Selection;
      this.curNetwork.setOptions(this.options);
   }


   setNodes(nodesToSet){
      let newNodes = new Map(nodesToSet.map((i) => [i.id, i]));
      let oldNodes = new Map(this.curNodes.map((i) => [i.id, i]));
      let toAddNodes = [];
      let toRemoveNodes = [];
      for(let node of newNodes.values()){
         if(!oldNodes.has(node.id)) toAddNodes.push(node);
      }
      this.curNodes.add(toAddNodes);
      for(let node of oldNodes.values()){
         if(!newNodes.has(node.id)) toRemoveNodes.push(node.id);
      }
      this.curNodes.remove(toRemoveNodes);
   }

   setEdges(edgesToSet){
      let newEdges = new Map(edgesToSet.map((i) => [i.id, i]));
      let oldEdges = new Map(this.curEdges.map((i) => [i.id, i]));
      let toAddEdges = [];
      let toRemoveEdges = [];
      for(let edge of newEdges.values()){
         if(!oldEdges.has(edge.id)) toAddEdges.push(edge);
      }
      this.curEdges.add(toAddEdges);
      for(let edge of oldEdges.values()){
         if(!newEdges.has(edge.id)) toRemoveEdges.push(edge.id);
      }
      this.curEdges.remove(toRemoveEdges);
   }
   

   startLink(){
      this.linkStartNode = this.curHoverNode;
      this.linkStartPoint = this.curNetwork.getPositions(this.linkStartNode)[this.linkStartNode];
      this.prevMode = this.curMode;
      this.curMode = GraphMode.Link;
   }
}

let instance = new GraphManager;
exports.graphManager = instance;
